/* eslint-disable react-hooks/exhaustive-deps */
import React, { KeyboardEventHandler, useCallback, useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ActionMeta, MultiValue } from "react-select";
import CreatableSelect from "react-select/creatable";
import LoadingButton from "../../components/button/LoadingButton/LoadingButton";
import Loading from "../../components/general/Loading";
import { errorToast, successToast } from "../../components/general/ToasterContainer";
import { InputField } from "../../components/InputField";
import { getFacilitator, getParticipants } from "../../functions/FilterUsers";
import confirmed from '../../images/check.png';
import AuthenticatedLayout from "../../layout/AuthenticatedLayout";
import * as FeedbackService from "../../services/FeedbackService";
import { useAppSelector } from "../../stores/hooks";
import { RootState } from "../../stores/store";
import "./FeedbackForm.scss";
import RadioButton from "../../components/RadioButton";
import { t } from "i18next";

const components = {
  DropdownIndicator: null,
};

const FeedbackForm = (): React.JSX.Element => {
  let { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const session = useAppSelector((state: RootState) => state.session);
  const [loading, setLoading] = useState<boolean>(true);
  const [formData, setFormData] = useState<IFeedback>({} as IFeedback);
  const [invitationEmailInputValue, setInvitationEmailInputValue] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [facilitator, setFacilitator] = useState<IUser>({} as IUser);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "submitting" | "submitted">("idle");

  const setFacilitatorDefaultData = () => {
    return {
      id: "",
      name: "",
      email: "",
      connected: false,
      role: 'facilitator',
      invitation_status: 'pending',
      invitation: {
        code: crypto.randomUUID()
      }
    } as IUser
  }

  const loadFeedback = useCallback(async () => {
    const setDefaultData = (): void => {
      setFormData({
        id: "",
        schedule_date: "",
        schedule_time: "",
        users: [
          {
            id: "",
            cognito_id: session?.id,
            name: session?.name,
            email: session?.email,
            photo_url: session?.picture,
            connected: false,
            role: 'requester',
            invitation_status: 'accepted',
            invitation: {
              code: crypto.randomUUID()
            }
          } as IUser,
          setFacilitatorDefaultData()
        ] as IUser[],
        identify_user_on_card: true,
        evaluation_level: (location.state as any)?.board_type,
        board_type: (location.state as any)?.board_type,
        visible: true,
        created_at: Date.now(),
      } as IFeedback);
    };

    const getError = (_data: any): void => {
      setDefaultData();
      setLoading(false);
    };

    const getSuccess = (data: any): void => {
      const facilitator = getFacilitator(data.users)
      setFacilitator(facilitator || setFacilitatorDefaultData())
      setFormData(data);
      setLoading(false);
    };

    if (id) {
      await FeedbackService.get(id, getSuccess, getError);
    } else {
      setDefaultData();
      setLoading(false);
    }
  }, [id, session, location.state]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  useEffect(() => {
    // Define evaluation_level automaticamente baseado no board_type quando selecionado
    // Apenas para novos feedbacks (sem id) - quando o board_type é selecionado no SelectCanvasBoard
    if (formData.board_type && !id) {
      const boardType = formData.board_type;
      setFormData(prev => ({
        ...prev,
        evaluation_level: boardType
      }));
    }
  }, [formData.board_type, id]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangeFacilitator = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = event.target.name.split(".").pop() as string;

    const users = formData.users.map((user: IUser) => {
      if (user.role === 'facilitator') {
        return { ...user, [name]: event.target.value }
      }

      return user
    })

    setFormData({ ...formData, users: users })
    setFacilitator({ ...facilitator, [name]: event.target.value })
  };

  const updateFacilitator = (): void => {
    const newFacilitator = setFacilitatorDefaultData()
    const users = formData.users.map((user: IUser) => {
      if (user.role === 'facilitator') {
        return newFacilitator
      }

      return user
    })

    setFormData({ ...formData, users: users })
    setFacilitator(newFacilitator)
  }

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (!invitationEmailInputValue.trim()) return

    switch (event.key) {
      case "Enter":
        event.preventDefault()

        if (formData.users.map(user => user.email).includes(invitationEmailInputValue.trim()))
          return

        setFormData({
          ...formData,
          users: [
            ...formData.users,
            {
              id: "",
              name: "",
              email: invitationEmailInputValue.trim(),
              invitation: { code: crypto.randomUUID() },
              connected: false,
              role: "participant",
              invitation_status: 'pending',
            } as IUser
          ],
        })
        setInvitationEmailInputValue("");
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: boolean } = {
      schedule_date: !formData.schedule_date,
      schedule_time: !formData.schedule_time,
      facilitator: getFacilitator(formData.users)?.email ? false : true,
      invitations: getParticipants(formData.users).length === 0,
    };

    if (Object.values(newErrors).some((error) => error)) {
      setErrors(newErrors);

      return false;
    }

    setErrors({});

    return true;
  };

  const createdError = (data: any): void => {
    errorToast({ message: t("feedbackStatusActions.sendError") });
    console.log("Erro ao enviar formulário:", data);
  };

  const createdSuccess = (_data: any): void => {
    successToast({ message: t("feedbackStatusActions.success") });
    navigate(`/feedback/created`);
  };

  const updatedError = (data: any): void => {
    errorToast({ message: t("feedbackStatusActions.sendError") });
    console.log("Erro ao enviar Feedback:", data);
  };

  const updatedSuccess = (_data: any): void => {
    successToast({ message: t("feedbackStatusActions.updated") });
    navigate(`/feedbacks`);
  };

  const deletedError = (data: any): void => {
    errorToast({ message: t("feedbackStatusActions.deleteError") });
    console.log("Erro ao enviar Feedback:", data);
  };

  const deletedSuccess = (_data: any): void => {
    successToast({ message: t("feedbackStatusActions.deleted") });
    navigate(`/feedbacks`);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitStatus("submitting");

    // Garante que evaluation_level seja definido baseado no board_type se não estiver definido
    const dataToSubmit = {
      ...formData,
      evaluation_level: formData.evaluation_level || formData.board_type
    };

    const callback = id ? FeedbackService.update : FeedbackService.create;
    const successCallback = (data: any) => {
      setSubmitStatus("submitted");
      id ? updatedSuccess(data) : createdSuccess(data);
    };
    const errorCallback = (data: any) => {
      setSubmitStatus("idle");
      id ? updatedError(data) : createdError(data);
    };

    callback(dataToSubmit, successCallback, errorCallback);
  };

  const destroyFeedback = () => {
    if (id) {
      confirmAlert({
        title: t("feedback.title.delete"),
        message: t("feedback.form.instructions.delete"),
        buttons: [
          {
            label: "Sim",
            onClick: () => FeedbackService.destroy(id, deletedSuccess, deletedError),
          },
          {
            label: "Não",
            onClick: () => {},
          },
        ],
      });
    }
  };

  const countCardsByUser = (user : IUser) => {
    if (!formData.cards) return 0

    const cards = formData?.cards?.filter((card: ICard) => {
      return card.owner === user.cognito_id
    });

    return cards?.length
  }


  const prepareCreatableSelectData = () => {
    return getParticipants(formData.users).map(
      (participant: IUser) =>
      ({
        label: participant.confirmed_at ? `${participant.email} ✅` : participant.email,
        value: participant.invitation.code,
        isFixed: countCardsByUser(participant) > 0,
      } as IReactSelectOptionItem)
    )
  };

  const handleChangeInvitations = (_newValue: MultiValue<IReactSelectOptionItem>, actionType: ActionMeta<IReactSelectOptionItem>) => {
    if (actionType.action === 'remove-value' && actionType.removedValue) {
      const removedValue = actionType.removedValue
      const users = formData.users.filter((user: IUser) => {
        return user.email !== removedValue.label.split(" ")[0] && user.invitation?.code !== removedValue.value
      });
      setFormData({ ...formData, users: users });
    }

    if (actionType.action === 'select-option') {
      const selectedOption = actionType.option

      const users = [...formData.users,
      {
        id: "",
        name: "",
        email: selectedOption?.label,
        invitation: { code: selectedOption?.value },
        connected: false,
        role: "participant",
        invitation_status: 'pending',
      } as IUser
      ]

      setFormData({ ...formData, users: users });
    }
  };

  const minimalTime = (schedule_date: string): string => {
    const currentDate = new Date()
    const today = currentDate.toISOString().split("T")[0];
    if (schedule_date !== today) {
      return "00:00"
    }

    const formatTimeUnit = (unit: number) => unit.toString().padStart(2, "0")
    const hours = formatTimeUnit(currentDate.getHours())
    const minutes = formatTimeUnit(currentDate.getMinutes())

    return `${hours}:${minutes}`
  }

  const storedEmails = localStorage.getItem("eventEmails");
  const emailOptionList: IReactSelectOptionItem[] = storedEmails
    ? JSON.parse(storedEmails).map((email: string) => ({ label: email, value: crypto.randomUUID() }))
    : [];
  if (loading)
    return (
      <AuthenticatedLayout>
        <div id="feedback-list" className="d-flex flex-column align-items-center justify-content-center">
          <Loading title="Carregando dados do formulário..." />
        </div>
      </AuthenticatedLayout>
    );
  const identifyUserOnCardDefault = String(formData?.identify_user_on_card) === "" || formData?.identify_user_on_card === undefined ? false : formData.identify_user_on_card;

  return (
    <AuthenticatedLayout>
      <div className="d-flex justify-content-center align-self-center">
        <div className="d-flex flex-column form-container">
          <h1 className="mb-5">{t("feedbackForm.title")}</h1>
          <form className="d-flex flex-column justify-content-start gap-4" onSubmit={handleSubmit}>
            <InputField
              label={t("feedbackForm.fields.whenFeedback")}
              type="date"
              id="schedule_date"
              placeholder={t("feedbackForm.instructions.selectDate")}
              name="schedule_date"
              value={formData.schedule_date}
              onChange={handleChange}
              onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
              isError={errors.schedule_date}
              min={new Date().toISOString().split("T")[0]}
            />
            <InputField
              label={t("feedbackForm.fields.whatTime")}
              type="time"
              id="schedule_time"
              name="schedule_time"
              placeholder={t("feedbackForm.instructions.selectTime")}
              value={formData.schedule_time}
              onChange={handleChange}
              isError={errors.schedule_time}
              min={minimalTime(formData.schedule_date)}
              onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
            />
            <InputField
              label={t("feedbackForm.fields.whoFacilitator")}
              type="email"
              id="facilitator"
              name="facilitador.email"
              placeholder={t("feedbackForm.instructions.emailFacilitator")}
              value={facilitator.email}
              onChange={handleChangeFacilitator}
              spanText={t("feedbackForm.instructions.emailFacilitator")}
              isError={errors.facilitator}
              disabled={facilitator.confirmed_at ? true : false}
            >
              {facilitator.confirmed_at && (
                <div className="d-flex align-items-center gap-1 mt-1">
                  <img src={confirmed} alt={t("feedbackList.status.confirmed")} width={12} height={12} />
                  <small>{t("feedback.form.fields.confirmed")}</small>
                  <button onClick={updateFacilitator} className="btn btn-sm btn-link">
                    {t("feedback.form.fields.update")}
                  </button>
                </div>
              )}
            </InputField>

            <RadioButton
              label={t("feedbackForm.fields.identifyParticipants")}
              name="identify_user_on_card"
              options={[
                { label: t("feedbackForm.buttons.options.yes"), value: true },
                { label: t("feedbackForm.buttons.options.no"), value: false },
              ]}
              handleChangeFn={handleChange}
              defaultValue={String(identifyUserOnCardDefault)}
            />

            <div className="input-group-form">
              <div className="d-flex flex-column mb-2">
                <label htmlFor="invitations">{t("feedbackForm.fields.whoParticipants")}</label>
                <span className="text-muted small">{t("feedbackForm.instructions.emailParticipants")}</span>
              </div>
              <CreatableSelect
                isClearable
                isMulti
                name="invitations"
                placeholder={t("feedbackForm.fields.participantPlaceholder")}
                components={components}
                inputValue={invitationEmailInputValue}
                onInputChange={(newValue) => setInvitationEmailInputValue(newValue)}
                onKeyDown={handleKeyDown}
                value={prepareCreatableSelectData()}
                onChange={handleChangeInvitations}
                className={`${errors.invitations ? "is-invalid" : ""}`}
                options={emailOptionList}
                formatCreateLabel={(inputValue) =>
                  `${t("feedback.form.fields.add")} "${inputValue}" ${t("feedback.form.fields.list")}`
                }
                styles={{
                  multiValue: (base, { data }) => {
                    return data.isFixed ? { ...base, backgroundColor: "#c0e0e0", paddingRight: "8px" } : base
                  },
                  multiValueRemove: (base, { data }) => {
                    return data.isFixed ? { ...base, display: "none" } : base
                  },
                  control: (provided, state) => ({
                    ...provided,
                    border: "#9E9B9B 1px solid",
                    borderRadius: "5px",
                    display: "flex",
                    flexWrap: "wrap",
                    maxWidth: "100%",
                    alignItems: "center",
                    margin: "0",
                    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
                    "&:hover": {
                      border: "#9E9B9B 1px solid",
                    },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    maxHeight: "100px",
                    overflowY: "auto",
                    zIndex: 9999,
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    maxHeight: "100px",
                    overflowY: "auto",
                    padding: "0",
                  }),
                  option: (provided) => ({
                    ...provided,
                    fontSize: "14px",
                    padding: "8px 12px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
                }}
              />
              {errors.invitations && <div className="invalid-feedback">{t("feedback.form.fields.required")}</div>}
            </div>
            <div className="row fs-3 mb-5">
              <div className="justify-content-between d-flex align-items-center">
                <div className="d-flex gap-2">
                  <LoadingButton
                    type="submit"
                    className="btn btn-primary"
                    status={submitStatus}
                    idleText={t("feedbackForm.buttons.send")}
                    submittingText={t("feedbackForm.buttons.status.sending")}
                    submittedText={t("feedbackForm.buttons.status.scheduled")}
                  />
                  <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                    {t("feedbackForm.buttons.cancel")}
                  </button>
                </div>
                <div>
                  {id && (
                    <div className="col text-end">
                      <button type="button" className="btn btn-danger" onClick={destroyFeedback}>
                        {t("buttonActions.delete")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AuthenticatedLayout>
  )
};

export default FeedbackForm;
